// src/components/programs/ProgramForm/helpers/formValidator.js

import { isValidDateRange } from "../../../../utils/programs/dateCalculations";
import { getValidationMessages } from "./formValidationMessages";

/**
 * Validate specific step
 */
export const validateStep = (step, formData, isEdit, isArabic, activeLang = null) => {
    const errors = {};
    const messages = getValidationMessages(isArabic);

    if (step === 0) {
        // ============================================
        // Step 0: Program Info
        // ============================================
        // Title (multilingual): require at least one language. If a language is currently active (visible), require it.
        const titleAr = formData.title?.ar?.trim();
        const titleEn = formData.title?.en?.trim();

        if (!titleAr && !titleEn) {
            // No title in any language -> mark the active language field if available, otherwise English
            const key = activeLang === 'ar' ? 'title_ar' : 'title_en';
            errors[key] = activeLang === 'ar' ? messages.title_ar_required : messages.title_en_required;
        } else {
            // If active language is visible and empty, require it specifically
            if (activeLang === 'ar' && !titleAr) {
                errors.title_ar = messages.title_ar_required;
            }
            if (activeLang === 'en' && !titleEn) {
                errors.title_en = messages.title_en_required;
            }
        }

        // Description (customer name) - multilingual logic similar to title
        const descAr = formData.description?.ar?.trim();
        const descEn = formData.description?.en?.trim();

        if (!descAr && !descEn) {
            const key = activeLang === 'ar' ? 'description_ar' : 'description_en';
            errors[key] = activeLang === 'ar' ? messages.description_ar_required : messages.description_en_required;
        } else {
            if (activeLang === 'ar' && !descAr) {
                errors.description_ar = messages.description_ar_required;
            }
            if (activeLang === 'en' && !descEn) {
                errors.description_en = messages.description_en_required;
            }
        }

        // Agent Name - multilingual
        const agentNameAr = formData.agent_name?.ar?.trim();
        const agentNameEn = formData.agent_name?.en?.trim();

        if (!agentNameAr && !agentNameEn) {
            const key = activeLang === 'ar' ? 'agent_name_ar' : 'agent_name_en';
            errors[key] = activeLang === 'ar' ? messages.agent_name_ar_required : messages.agent_name_en_required;
        } else {
            if (activeLang === 'ar' && !agentNameAr) {
                errors.agent_name_ar = messages.agent_name_ar_required;
            }
            if (activeLang === 'en' && !agentNameEn) {
                errors.agent_name_en = messages.agent_name_en_required;
            }
        }

        // Cities - multilingual
        const citiesAr = formData.cities?.ar?.trim();
        const citiesEn = formData.cities?.en?.trim();

        if (!citiesAr && !citiesEn) {
            const key = activeLang === 'ar' ? 'cities_ar' : 'cities_en';
            errors[key] = activeLang === 'ar' ? messages.cities_ar_required : messages.cities_en_required;
        } else {
            if (activeLang === 'ar' && !citiesAr) {
                errors.cities_ar = messages.cities_ar_required;
            }
            if (activeLang === 'en' && !citiesEn) {
                errors.cities_en = messages.cities_en_required;
            }
        }

        if (!formData.start_date) {
            errors.start_date = messages.start_date_required;
        }

        if (!formData.end_date) {
            errors.end_date = messages.end_date_required;
        }

        if (formData.start_date && formData.end_date) {
            if (!isValidDateRange(formData.start_date, formData.end_date)) {
                errors.end_date = messages.end_date_invalid;
            }
        }

        const totalPax = formData.pax_types?.reduce((sum, pt) => sum + (pt.count || 0), 0);
        if (!totalPax || totalPax === 0) {
            errors.pax_types = messages.pax_types_required;
        }

        // if (!isEdit && !formData.cover_image) {
        //     errors.cover_image = messages.cover_image_required;
        // }

    } else if (step === 1) {
        // ============================================
        // Step 1: Itinerary
        // ============================================
        const hasProducts = formData.itinerary?.some((day) => day.products?.length > 0);
        if (!hasProducts) {
            errors.itinerary = messages.itinerary_required;
        }

        // Validate day names and dynamic fields
        formData.itinerary?.forEach((day, dayIndex) => {
            // ✅ Only validate day names if day has products
            if (day.products?.length > 0) {
                if (!day.day_name?.ar?.trim()) {
                    errors[`day_${dayIndex}_name_ar`] = messages.day_name_ar_required;
                }

                if (!day.day_name?.en?.trim()) {
                    errors[`day_${dayIndex}_name_en`] = messages.day_name_en_required;
                }

                // Validate dynamic fields for each product
                day.products.forEach((product) => {
                    const pricingCode = product.product_snapshot?.pricing_unit;
                    const instanceId = product.instance_id;

                    // ✅ Validate based on pricing_unit code
                    if (pricingCode === "per_person") {
                        // Per Person requires: pickup_time, city
                        if (!product.dynamic_fields?.pickup_time) {
                            errors[`${instanceId}_pickup_time`] = messages.pickup_time_required;
                        }

                        if (!product.dynamic_fields?.city?.trim()) {
                            errors[`${instanceId}_city`] = messages.city_required;
                        }

                    } else if (pricingCode === "per_night") {
                        // Per Night requires: nights (1 <= nights <= program_nights)
                        const nights = product.dynamic_fields?.nights;
                        // Allow nights to be zero or undefined (default) for per-night products.
                        // Only validate when a value is provided: it must be >= 0 and not exceed program nights.
                        if (nights != null) {
                            if (nights < 0) {
                                errors[`${instanceId}_nights`] = messages.nights_required;
                            } else if (nights > formData.durations_nights) {
                                errors[`${instanceId}_nights`] = messages.nights_exceeded(formData.durations_nights);
                            }
                        }

                    } else if (pricingCode === "per_unit") {
                        // Per Unit requires: time, units
                        if (!product.dynamic_fields?.time) {
                            errors[`${instanceId}_time`] = messages.time_required;
                        }

                        const units = product.dynamic_fields?.units;
                        if (!units || units < 1) {
                            errors[`${instanceId}_units`] = messages.units_required;
                        }
                    }
                });
            }
        });

    } else if (step === 2) {
        // ============================================
        // Step 2: Quotation
        // ============================================
        // Validate product-level discounts
        formData.itinerary?.forEach((day) => {
            day.products?.forEach((product) => {
                const instanceId = product.instance_id;
                const effectivePrice = product.pricing?.custom_price || product.pricing?.price;

                if (product.pricing?.discount_type === "amount") {
                    const discount = product.pricing?.discount_value;
                    if (discount && discount > effectivePrice) {
                        errors[`${instanceId}_discount`] = messages.discount_exceeded;
                    }

                } else if (product.pricing?.discount_type === "percent") {
                    const discount = product.pricing?.discount_value;
                    if (discount && (discount < 0 || discount > 100)) {
                        errors[`${instanceId}_discount`] = messages.discount_percent_invalid;
                    }
                }
            });
        });

        // Validate global discount
        if (formData.global_discount?.type === "percent") {
            const value = formData.global_discount?.value;
            if (value && (value < 0 || value > 100)) {
                errors.global_discount = messages.global_discount_invalid;
            }
        }
    }

    return errors;
};
